import { CheckGroupProps } from 'components/elements/form/CheckGroup'
import { CombinationGroupProps } from 'components/elements/form/CombinationGroup'
import { DivideCarFourGroupProps } from 'components/elements/form/DamagedPartGroup/DivideCarFourGroup'
import { RadioGroupProps } from 'components/elements/form/RadioGroup'
import { DIVIDE_CAR_FOUR_PART_KEYS } from 'components/elements/items/DamagedPartLine/DivideCarFourLine'
import {
  isRadioLineProps,
  RadioLineProps,
} from 'components/elements/items/RadioLine'
import { HistoryStackContextProps } from 'contexts/HistoryStackContext'
import dayjs from 'dayjs'
import { AccidentImage, QnEditStateProps } from 'states/QnState'
import deepcopy from 'ts-deepcopy'
import { StatesProps } from 'utils/commonHooks'
import { addHistory, getCurrentHistory, setHistoryAction } from 'utils/log'
import { isFail, mergeStatus, StatusProps, success } from 'utils/status'
import { OptionResource } from 'constants/qnState'
import { OptionItem } from 'components/elements/common'

/**
 * 関数がasyncか検証します。
 * Promiseにはfalseを返却するため注意してください。
 * @param func 検証する関数
 * @returns 関数がasync関数な場合trueを返却
 */
export const isAsync = (func: () => unknown): boolean => {
  return func.constructor.name === 'AsyncFunction'
}

/**
 * 1つ目のアクションが成功した場合、2つ目のアクションを実行します。（同期・非同期両対応）
 * @param firstAction 1つ目のアクション関数
 * @param secondAction 2つ目のアクション関数
 * @returns アクションの成否
 */
export const asyncActionConnect = async (
  firstAction: () => StatusProps | Promise<StatusProps>,
  secondAction: () => StatusProps | Promise<StatusProps>
): Promise<StatusProps> => {
  const firstActionStatus: StatusProps | Promise<StatusProps> = firstAction()

  // 1つ目が失敗すれば終了
  if (isFail(await firstActionStatus)) {
    return firstActionStatus
  }
  const secondActionStatus: StatusProps | Promise<StatusProps> = secondAction()

  // 1つ目と2つ目の結果をマージして返却
  return mergeStatus(await firstActionStatus, await secondActionStatus)
}

/**
 * アクションの成否に応じて遷移関数を実行
 * @param transition 遷移関数
 * @param actionName 操作履歴に記録するアクション名
 * @param action アクション関数
 * @returns アクションと遷移の成否
 */
export const transitionWithAction = async (
  historyStackContext: HistoryStackContextProps,
  actionName: string,
  transition: () => void,
  action?: () => StatusProps | Promise<StatusProps>
): Promise<StatusProps> => {
  // 遷移関数にステータスを返却させる
  const transitionWithStatus = (): StatusProps => {
    transition()
    return success()
  }

  // 現在の画面の操作履歴にactionを追加
  setHistoryAction(historyStackContext, actionName)

  const resultStatus = action
    ? await asyncActionConnect(action, transitionWithStatus)
    : transitionWithStatus()

  if (isFail(resultStatus)) {
    // アクション失敗時、操作履歴を積み上げる
    addHistory(historyStackContext, getCurrentHistory(historyStackContext).page)
  }

  return resultStatus
}

/**
 * 対象のラベルのラジオが選択されていればtrue, それ以外ならfalseを返却する。
 * @param radios Stateのラジオ配列
 * @param targetLabel チェック対象のラベル
 * @returns 対象ラベルの選択状況
 */
export const isSelectedRadio = (
  group: RadioGroupProps,
  targetLabel: string
): boolean => {
  return group.radios.some(
    (radio) => radio.isSelected && radio.label === targetLabel
  )
}

/**
 * 対象のラジオのうち、選択中のラベル（未選択の場合は''）を返却する。
 * @param radios Stateのラジオ配列
 * @returns 選択しているラベル
 */
export const getSelectedRadioLabel = (group: RadioGroupProps): string => {
  return group.radios.find((radio) => radio.isSelected)?.label || ''
}

/**
 * 対象のラベルのチェックが選択されていればtrue, それ以外ならfalseを返却する。
 * @param checks Stateのチェック配列
 * @param targetLabel チェック対象のラベル
 */
export const isSelectedCheck = (
  group: CheckGroupProps,
  targetLabel: string
): boolean => {
  return group.checks.some(
    (check) => check.isChecked && check.label === targetLabel
  )
}

/**
 * 対象のチェックのうち、選択中のラベル（未選択の場合は空の配列[]）を返却する。
 * @param checks Stateのチェック配列
 * @returns 選択しているチェック
 */
export const getSelectedCheckLabel = (group: CheckGroupProps): string[] => {
  return group.checks
    .filter((check) => check.isChecked)
    .map((check) => check.label)
}

/**
 * 対象のチェックのうち、選択中のものがないときにtrue, それ以外ならfalseを返却する。
 * @param checks Stateのチェック配列
 * @returns 選択中のものがないときにtrue, それ以外ならfalse
 */
export const isEmptyCheck = (group: CheckGroupProps): boolean => {
  return getSelectedCheckLabel(group).length === 0
}

/**
 * 対象の破損箇所のうち、選択中のラベル（未選択の場合は空の配列[]）を返却する。
 * @param checks Stateの破損箇所配列
 * @returns 選択している破損箇所
 */
export const getSelectedDamagedPartLabel = (
  group: DivideCarFourGroupProps
): string[] => {
  return DIVIDE_CAR_FOUR_PART_KEYS.filter(
    (partKey) => group.damagedPart[partKey].isDamaged
  ).map((partKey) => group.damagedPart[partKey].label)
}

/**
 * 対象のカレンダーが日付選択済みならtrue, それ以外ならfalseを返却する。
 * @param calendar カレンダーのState
 * @returns 日付選択済みならtrue, それ以外ならfalse
 */
// export const isSelectedCalendar = (group: CalendarGroupProps): boolean => {
//   return !!group.calendar.selectedDate
// }

/**
 * サロゲートペアを含んでいればtrue, それ以外ならfalseを返却する。
 * @param target
 */
export const containsSurrogatePair = (target: string): boolean => {
  const some = Array.prototype.some
  return some.call(target, (char) => {
    const charCode = char.charCodeAt(0)
    return (
      (0xd800 <= charCode && charCode <= 0xdbff) ||
      (0xdc00 <= charCode && charCode <= 0xdfff)
    )
  })
}

/**
 * 入力禁止文字を含んでいればtrue, それ以外ならfalseを返却する。
 * @param target
 */
export const containsIncompatibleChar = (target: string): boolean => {
  const INCOMPATIBLE_CHARS = ['"']
  return (
    containsSurrogatePair(target) ||
    INCOMPATIBLE_CHARS.some(
      (incompatibleChar) => target.indexOf(incompatibleChar) >= 0
    )
  )
}

/**
 * 入力系画面にてstateへの反映タイミングをずらすためにcloneしたstateを含むpropsを作成し返却する。
 * また、編集中項目がある場合はその値をstateに設定する。
 * @param props
 * @param editingItem
 */
export const createProps: (
  props: StatesProps,
  editingItem?: QnEditStateProps
) => StatesProps = (props, editingItem) => {
  return {
    state: { ...deepcopy(props.state), ...editingItem },
    setState: props.setState,
  }
}

/**
 * 対象の画像Stateの画像のうち、1つでも選択していればtrue, それ以外ならfalseを返却する
 * @param imageGroup 対象の画像State
 * @returns 画像を1つでも選択しているか
 */
export const hasSelectedImage = (imageGroup: AccidentImage): boolean => {
  return Object.values(imageGroup).some((image) => image.image)
}

/**
 * CombinationGroupのラジオのうち、対象のラベルが
 * 選択されていればtrue, それ以外ならfalseを返却する。
 * @param group 対象のCombinationGroup
 * @param targetLabel チェック対象のラベル
 * @returns 対象ラベルの選択状況
 */
export const isSelectedCombinationRadio = (
  group: CombinationGroupProps,
  targetLabel: string
): boolean => {
  return group.combination
    .map((line) => line.item)
    .filter((item): item is RadioLineProps => isRadioLineProps(item))
    .some((item) => item.isSelected && item.label === targetLabel)
}

/**
 * CombinationGroupのラジオのうち、選択中のラベル（未選択の場合は''）を返却する。
 * @param group 対象のCombinationGroup
 * @returns 選択しているラベル
 */
export const getSelectedCombinationRadioLabel = (
  group: CombinationGroupProps
): string => {
  return (
    group.combination
      .map((line) => line.item)
      .filter((item): item is RadioLineProps => isRadioLineProps(item))
      .find((item) => item.isSelected)?.label || ''
  )
}

/**
 * 文字列から改行とタブを削除して返却する
 * @param target 改行とタブを削除したい文字列
 * @returns 改行とタブが削除された文字列
 */
export const deleteReturnTabString = (target: string): string => {
  return target.replace(/\n/g, ' ').replace(/\t/g, ' ')
}

/**
 * Switchを式化した関数で、第一引数のオブジェクトの中に、
 * 第二引数のキーと一致するキーがあればそのキーの値を、
 * なければ第三引数を返却する。
 * @param obj Switchオブジェクト
 * @param key オブジェクトに一致するキー
 * @param defaultValue 一致するキーがないときに返却する値
 * @returns オブジェクトの中のキーが一致すれば、そのキーの値、それ以外はdefaultValueを返却する
 */
export const switchValue = <T>(
  obj: {
    [key: string]: T
  },
  key: string | undefined,
  defaultValue: T
): T => {
  return key && Object.keys(obj).includes(key) ? obj[key] : defaultValue
}

/**
 * 対象の2つが同じ日付かどうかチェックする。
 * @param target 1つ目の日時
 * @param target2 2つ目の日時
 * @returns 同じ日付ならtrue、それ以外はfalseを返却する
 */
export const isSameDay = (
  target: dayjs.Dayjs,
  target2: dayjs.Dayjs
): boolean => {
  return target.startOf('day').diff(target2.startOf('day')) === 0
}

/**
 * 文字列を半角 → 全角に変換する
 * @param target 半角から全角に変換したい文字列
 * @returns 全角に変換した文字列
 */
export const toFullWidthString = (target: string): string => {
  return target.replace(/[!-~]/g, (target) =>
    String.fromCharCode(target.charCodeAt(0) + 0xfee0)
  )
}

export function createOptions<T extends string>(
  options: OptionResource<T>
): OptionItem<T>[] {
  // EditingItemの初期値を[""]にする関係で、optionsに[""]が含まれている
  // UIに表示する際には不要のため[""]を除いたものを表示する
  const filteredOption = Object.entries(options).filter((option) =>
    option.indexOf('')
  )
  return filteredOption.map(
    ([k, v]) => ({ label: v, value: k } as OptionItem<T>)
  )
}

/**
 * 文字列を全角 → 半角に変換する
 * @param target 全角から半角に変換したい文字列
 * @returns 半角に変換した文字列
 */
export const toHalfWidthString = (target: string): string => {
  return String(target).replace(/[！-～]/g, function (all) {
    return String.fromCharCode(all.charCodeAt(0) - 0xfee0)
  })
}

// 半角ｶﾅマッピング
// prettier-ignore
const HALF_KATA = [
  'ｧ','ｱ','ｨ','ｲ','ｩ','ｳ','ｪ','ｴ','ｫ','ｵ',
  'ｶ','ｶﾞ','ｷ','ｷﾞ','ｸ','ｸﾞ','ｹ','ｹﾞ','ｺ','ｺﾞ',
  'ｻ','ｻﾞ','ｼ','ｼﾞ','ｽ','ｽﾞ','ｾ','ｾﾞ','ｿ','ｿﾞ',
  'ﾀ','ﾀﾞ','ﾁ','ﾁﾞ','ｯ','ﾂ','ﾂﾞ','ﾃ','ﾃﾞ','ﾄ','ﾄﾞ',
  'ﾅ','ﾆ','ﾇ','ﾈ','ﾉ',
  'ﾊ','ﾊﾞ','ﾊﾟ','ﾋ','ﾋﾞ','ﾋﾟ','ﾌ','ﾌﾞ','ﾌﾟ','ﾍ','ﾍﾞ','ﾍﾟ','ﾎ','ﾎﾞ','ﾎﾟ',
  'ﾏ','ﾐ','ﾑ','ﾒ','ﾓ',
  'ｬ','ﾔ','ｭ','ﾕ','ｮ','ﾖ',
  'ﾗ','ﾘ','ﾙ','ﾚ','ﾛ',
  'ヮ','ﾜ','ヰ','ヱ','ｦ','ﾝ','ｳﾞ','ヵ','ヶ','ﾜﾞ','ｲﾞ','ｴﾞ','ｦﾞ'
]

/** 全角カナを半角ｶﾅに変換する */
export function convertWideKanaToHalfKana(text: string): string {
  text = text
    .replace(/[ァ-ヺ]/g, (m) => HALF_KATA[m.charCodeAt(0) - 12449])
    .replace(/[ぁ-ゖ]/g, (m) => HALF_KATA[m.charCodeAt(0) - 12353])
    // eslint-disable-next-line no-irregular-whitespace
    .replace(/[゛゜ー。「」、・　]/g, (m) =>
      'ﾞﾟｰ｡｢｣､･ '.charAt('゛゜ー。「」、・　'.indexOf(m))
    )
  return toHalfWidthString(text)
}

/**
 * 連番のリストを取得する
 * @param start 数値の開始位置
 * @param end 数値の終了位置
 * @returns start～end内の連番のリスト
 */
export function range(start: number, end: number): Array<number> {
  return Array.from({ length: end - start + 1 }, (v, k) => k + start)
}

/**
 * 半角カタカナ/英数字の文字列を全角に変換する
 * @param target
 * @returns
 */
export function convertFullKatakana(target: string): string {
  const D_MUD = 'ガギグゲゴザジズゼゾダヂヅデドバビブベボパピプペポヴヷヺ'
  const S_MUD = 'ｶﾞｷﾞｸﾞｹﾞｺﾞｻﾞｼﾞｽﾞｾﾞｿﾞﾀﾞﾁﾞﾂﾞﾃﾞﾄﾞﾊﾞﾋﾞﾌﾞﾍﾞﾎﾞﾊﾟﾋﾟﾌﾟﾍﾟﾎﾟｳﾞﾜﾞｦﾞ'
  const D_KIY =
    'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホ' +
    'マミムメモヤユヨラリルレロワヲンァィゥェォッャュョ。、ー「」・　'
  const S_KIY = 'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜｦﾝｧｨｩｪｫｯｬｭｮ｡､ｰ｢｣･ '

  // S_MUD(半角カタカナ)をD_MUD(全角カタカナ)に変換
  for (let i = 0, len = D_MUD.length; i < len; i++) {
    target = target
      .split(S_MUD.slice(i * 2, i * 2 + 2))
      .join(D_MUD.slice(i, i + 1))
  }

  // S_MUD(半角カタカナ(濁点、記号))をD_MUD(全角カタカナ(濁点、記号))に変換
  for (let i = 0, len = D_KIY.length; i < len; i++) {
    target = target.split(S_KIY.slice(i, i + 1)).join(D_KIY.slice(i, i + 1))
  }
  return toFullWidthString(target) // カタカナを全角に変換したものをtoFullWidthStringで半角英数字を全角変換したものをreturnする。
}
/**
 * 氏名のバリデーション
 * @param lastName 姓
 * @param firstName 名
 * @param errorMessageKanaType エラーメッセージ
 * @returns エラーメッセージ
 */

export function validateFullName(
  lastName: string,
  firstName: string,
  errorMessageKanaType: string
): string {
  const kanaRegex = /^[ァ-ヶーｦ-ﾟ]+$/
  if (
    (lastName && !firstName) ||
    (!lastName && firstName) ||
    (!kanaRegex.test(lastName) && lastName) ||
    (!kanaRegex.test(firstName) && firstName)
  ) {
    return errorMessageKanaType
  }
  return ''
}

/**
 * 電話番号のバリデーション
 * @param firstNumber 1枠名（3桁）
 * @param secondNumber 1枠名（4桁）
 * @param lastNumber 1枠名（4桁）
 * @param errorMessageHalfwidthFullwidthNumberType バリデーションエラーメッセージ
 * @param errorMessagePhoneNumberLength 桁数エラーメッセージ
 * @returns エラーメッセージ
 */

export function validatePhoneNumber(
  firstNumber: string,
  secondNumber: string,
  lastNumber: string,
  errorMessageHalfwidthFullwidthNumberType: string,
  errorMessagePhoneNumberLength: string
): string {
  if (!firstNumber && !secondNumber && !lastNumber) {
    return ''
  }
  if (
    !firstNumber.match(/^[0-9０-９]*$/) ||
    !secondNumber.match(/^[0-9０-９]*$/) ||
    !lastNumber.match(/^[0-9０-９]*$/)
  ) {
    return errorMessageHalfwidthFullwidthNumberType
  }
  if (
    firstNumber.length < 3 ||
    secondNumber.length < 4 ||
    lastNumber.length < 4
  ) {
    return errorMessagePhoneNumberLength
  }
  return ''
}

export type sepalateNameParts = {
  first: string
  last: string
}

/**
 * 氏名の文字列を全角に変換しスペースを起点に姓と名に分割
 * @param target
 * @returns last（姓）first(名）
 */
export function splitFullWidthName(target: string): sepalateNameParts {
  const nameSplitBlank = convertFullKatakana(target).split(/\s+/)
  const [last, first] = nameSplitBlank
  // 姓名の間にスペースがない（firstがundifinde）時、firstに空文字を格納
  if (!first) {
    return {
      last: last,
      first: '',
    }
  }
  return { last, first }
}

export type sepalateNumberParts = {
  first: string
  second: string
  last: string
}

/**
 * 11桁の電話番号を3桁、4桁、4桁に分割した配列に変換する
 * @param target
 * @returns first（3桁）,second（4桁）,last（4桁）
 */
export function sepalatePhoneNumber(target: string): sepalateNumberParts {
  // 電話番号から数字以外の文字列を削除
  const cleaned = target.replace(/[^0-9]/g, '')
  const pattern = /^(\d{3})(\d{4})(\d{4})$/
  const match = cleaned.match(pattern)
  if (!match) {
    return {
      first: '',
      second: '',
      last: '',
    }
  }
  const [, first, second, last] = match
  return { first, second, last }
}

export type sepalatePlateNumberParts = {
  joinPlateNumber: string
  region: string
  classNum: string
  kana: string
  design: string
}

/**
 * ナンバープレートの文字列を全角に変換し地域名, 分類番号、平仮名等、一連指定番号に分割
 * @param target
 * @returns region(地域名), classNum(分類番号), kana(平仮名等), design(一連指定番号)
 */
export function splitFullWidthPlateNumber(
  target: string
): sepalatePlateNumberParts {
  const pattern = /^([ァ-ヶｦ-ﾟ\u3040-\u309F]+)([0-9０-９a-zA-ZＡ-Ｚａ-ｚ]+)([ァ-ヶｦ-ﾟ\u3040-\u309F]+)([0-9０-９a-zA-ZＡ-Ｚａ-ｚ]+)$/
  const converted = convertFullKatakana(target)
  const match = converted.match(pattern)
  function addAZeroBefore(target: string): string {
    return target && target.length < 4 ? target.padStart(4, '０') : target
  }
  if (!match) {
    return {
      joinPlateNumber: converted,
      region: converted,
      classNum: '',
      kana: '',
      design: '',
    }
  }
  const [, region, classNum, kana, designRaw] = match
  const design = addAZeroBefore(designRaw)
  const joinPlateNumber = `${region}${classNum}${kana}${design}`
  return { joinPlateNumber, region, classNum, kana, design }
}
